import * as React from 'react';
import { t } from '@reos/i18n';
import type { ReviewLevel } from '../lib/audit-api.js';

const cs = { locale: 'cs' } as const;

/**
 * KROK ZDARMA1 — DVE JASNE VOLBY U PROVĚŘENÍ (rozhodnuti R.B. 14. 10. 2026:
 * „zakladni proverení zdarma ano — dela to i jina konkurence").
 *
 * Do tohoto kroku melo UI JEDNO tlacitko a potvrzeni „je to placeny beh —
 * odecte se z kreditu kancelare", prestoze bezplatna uroven v API existovala
 * od kroku ZR1 (`reportLevel: 'zakladni'`). Makler se tedy k bezplatnemu behu
 * nemel jak dostat.
 *
 * Komponenta je JEDNA pro vsechna misto, ktera beh spousti (karta „Prověřit
 * nemovitost" u zakazky bez behu i „Prověřit znovu" v karte Prověření) —
 * dve kopie teze volby by se rozesly prave v tom, co se o cene rika.
 *
 * VYCHOZI a ZVYRAZNENA je BEZPLATNA uroven (zadani bod 1).
 */

/**
 * KROK NOC6 body 3 a 4 (nalezy N27, N28) — CISLA, KTERA MA MAKLER VIDET
 * PREDEM.
 *
 * `null` = cisla jeste nejsou (nacitaji se, nebo je endpoint nedal); pak se
 * popisek NEMENI — zadna vymyslena cena, zadny vymysleny zbytek stropu.
 */
export interface ReviewOptions {
  readonly basic: { readonly limit: number | null; readonly remaining: number | null };
  readonly full: {
    readonly priceCzk: string | null;
    readonly currency: string;
    readonly creditBalanceCzk: string;
    readonly creditSufficient: boolean;
  };
}

/** Castka pro cloveka — cele koruny, tentyz tvar jako v kartach zakazky. */
export function reviewMoney(amount: string | null, currency: string): string | null {
  if (amount === null) return null;
  const value = Number(amount);
  if (!Number.isFinite(value)) return null;
  return new Intl.NumberFormat('cs-CZ', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

/** Texty potvrzeni podle urovne — placena mluvi o kreditu, bezplatna NE. */
export function reviewConfirmBodyFor(
  level: ReviewLevel,
  publicDemo: boolean,
  options: ReviewOptions | null = null,
): string {
  /*
   * Krok DEMO7 bod 1: na verejnem demu prevereni NIC nestoji (prehraje se
   * pripraveny vysledek), takze „placeny beh" by byl nepravdivy — demo text
   * ma prednost pred urovni.
   */
  if (publicDemo) return t('webAdmin.caseDetailV4.startReviewConfirmDemo', cs);
  if (level === 'zakladni') return t('webAdmin.caseDetailV4.startReviewConfirmBasic', cs);
  /*
   * Krok NOC6 bod 3 (N27): potvrzeni placeneho behu rika KONKRETNI cenu
   * a zustatek. Bez cisel zustava puvodni veta — slibit „odečte se z kreditu"
   * umime vzdy, cislo jen kdyz ho opravdu mame.
   */
  const price = reviewMoney(options?.full?.priceCzk ?? null, options?.full?.currency ?? 'CZK');
  const balance = reviewMoney(options?.full?.creditBalanceCzk ?? null, options?.full?.currency ?? 'CZK');
  if (price === null || balance === null) return t('webAdmin.caseDetailV4.startReviewConfirm', cs);
  return t('webAdmin.caseDetailV4.startReviewConfirmFull', { ...cs, values: { price, balance } });
}

export function ReviewLevelChoice({
  value,
  onChange,
  disabled = false,
  testId = 'review-level',
  options = null,
  hideBasicQuota = false,
}: {
  readonly value: ReviewLevel;
  readonly onChange: (next: ReviewLevel) => void;
  readonly disabled?: boolean;
  /** Dve mista na jedne strance nesmi mit tytez `name`/`id` prepinacu. */
  readonly testId?: string;
  /** Krok NOC6: cena, kredit a zbytek stropu z `GET …/review-options`. */
  readonly options?: ReviewOptions | null;
  /**
   * Krok NOC6 bod 4: demo kancelar strop nema (beh se vubec nezaradi, DEMO7c
   * prehraje pripraveny vysledek), takze by „zbývá N z 30" lhalo.
   */
  readonly hideBasicQuota?: boolean;
}): React.JSX.Element {
  const price = reviewMoney(options?.full?.priceCzk ?? null, options?.full?.currency ?? 'CZK');
  const balance = reviewMoney(options?.full?.creditBalanceCzk ?? null, options?.full?.currency ?? 'CZK');
  /** Cislo stropu se tiskne jen kdyz ho mame OBE a strop na kancelar plati. */
  /*
   * `options` prichazi z API, takze se o jeho tvaru NIC nepredpoklada: atrapa
   * v testech obrazovky i starsi server muzou poslat objekt bez `full`/`basic`
   * a volba se tim nesmi polozit (zivy nalez v `zakazka-detail.spec.tsx`).
   */
  const quota =
    hideBasicQuota ||
    options?.basic?.limit === null ||
    options?.basic?.limit === undefined ||
    options.basic.remaining === null
      ? null
      : { remaining: String(options.basic.remaining), limit: String(options.basic.limit) };
  return (
    <fieldset
      className="rh-v4-next__note"
      style={{ display: 'block', border: 0, margin: 0, padding: 0 }}
      data-testid={testId}
    >
      <legend className="rh-a2-meta" style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {t('webAdmin.caseDetailV4.reviewLevelLegend', cs)}
      </legend>
      {/* Bezplatna uroven je PRVNI a zvyraznena stitkem „Zdarma". */}
      <label
        className="flex items-start gap-2 mt-2"
        data-testid={`${testId}-basic`}
        data-selected={String(value === 'zakladni')}
        style={value === 'zakladni' ? { fontWeight: 'var(--rh-heading-weight)' } : undefined}
      >
        <input
          type="radio"
          name={testId}
          value="zakladni"
          checked={value === 'zakladni'}
          disabled={disabled}
          onChange={() => { onChange('zakladni'); }}
          data-testid={`${testId}-basic-input`}
        />
        <span>
          {t('webAdmin.caseDetailV4.reviewLevelBasicTitle', cs)}{' '}
          <span className="rh-a2-badge rh-a2-badge--ok" data-testid={`${testId}-basic-badge`}>
            {t('webAdmin.caseDetailV4.reviewLevelBasicBadge', cs)}
          </span>
          <span className="rh-a2-meta" style={{ display: 'block', fontWeight: 'normal' }}>
            {t('webAdmin.caseDetailV4.reviewLevelBasicNote', cs)}
          </span>
          {/* Krok NOC6 bod 4 (N28): strop se makler dozvedel az chybou 429. */}
          {quota === null ? null : (
            <span
              className="rh-a2-meta"
              style={{ display: 'block', fontWeight: 'normal' }}
              data-testid={`${testId}-basic-quota`}
            >
              {t('webAdmin.caseDetailV4.reviewLevelBasicRemaining', { ...cs, values: quota })}
            </span>
          )}
        </span>
      </label>
      <label
        className="flex items-start gap-2 mt-2"
        data-testid={`${testId}-full`}
        data-selected={String(value === 'plny')}
      >
        <input
          type="radio"
          name={testId}
          value="plny"
          checked={value === 'plny'}
          disabled={disabled}
          onChange={() => { onChange('plny'); }}
          data-testid={`${testId}-full-input`}
        />
        <span>
          {t('webAdmin.caseDetailV4.reviewLevelFullTitle', cs)}
          <span className="rh-a2-meta" style={{ display: 'block' }}>
            {t('webAdmin.caseDetailV4.reviewLevelFullNote', cs)}
          </span>
          {/*
            Krok NOC6 bod 3 (N27): KONKRETNI cena z katalogu a zustatek kreditu,
            ze ktereho se plati. Kdyz kredit nestaci, rekne to volba PREDEM —
            ne az selhany beh.
          */}
          {price === null || balance === null ? null : (
            <span
              className="rh-a2-meta"
              style={
                options?.full?.creditSufficient === false
                  ? { display: 'block', color: 'var(--rh-warning-strong)' }
                  : { display: 'block' }
              }
              data-testid={`${testId}-full-price`}
              data-credit-sufficient={String(options?.full?.creditSufficient !== false)}
            >
              {t(
                options?.full?.creditSufficient === false
                  ? 'webAdmin.caseDetailV4.reviewLevelFullCreditShort'
                  : 'webAdmin.caseDetailV4.reviewLevelFullPrice',
                { ...cs, values: { price, balance } },
              )}
            </span>
          )}
        </span>
      </label>
    </fieldset>
  );
}
